export const PWAUpdateSeverity = {
  Normal: 'normal',
  Critical: 'critical',
} as const

export type PWAUpdateSeverity = (typeof PWAUpdateSeverity)[keyof typeof PWAUpdateSeverity]

export const PWAUpdateTestMode = {
  Normal: PWAUpdateSeverity.Normal,
  Critical: PWAUpdateSeverity.Critical,
  CriticalNow: 'criticalNow',
} as const

export type PWAUpdateTestMode = (typeof PWAUpdateTestMode)[keyof typeof PWAUpdateTestMode]

export type PWAUpdatePolicy = {
  severity: PWAUpdateSeverity
  snoozeMinutes: number
  deadlineMinutes?: number
}

export const DEFAULT_PWA_UPDATE_POLICY: PWAUpdatePolicy = {
  severity: PWAUpdateSeverity.Normal,
  snoozeMinutes: 10,
  deadlineMinutes: 60,
}

export const PWA_UPDATE_POLICY_URL = '/pwa-update-policy.json'

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const getPositiveNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined
  return value
}

export const normalizePWAUpdatePolicy = (policy: PWAUpdatePolicy): PWAUpdatePolicy => {
  if (!policy.deadlineMinutes || policy.snoozeMinutes <= policy.deadlineMinutes) return policy

  return {
    ...policy,
    snoozeMinutes: policy.deadlineMinutes,
  }
}

export const parsePWAUpdatePolicy = (value: unknown, fallback: PWAUpdatePolicy = DEFAULT_PWA_UPDATE_POLICY): PWAUpdatePolicy => {
  if (!isRecord(value)) return fallback

  const severity = value.severity === PWAUpdateSeverity.Critical ? PWAUpdateSeverity.Critical : PWAUpdateSeverity.Normal
  const snoozeMinutes = getPositiveNumber(value.snoozeMinutes) ?? fallback.snoozeMinutes
  const deadlineMinutes = getPositiveNumber(value.deadlineMinutes) ?? fallback.deadlineMinutes

  return normalizePWAUpdatePolicy({
    severity,
    snoozeMinutes,
    ...(deadlineMinutes ? { deadlineMinutes } : {}),
  })
}

export const fetchPWAUpdatePolicy = async (): Promise<PWAUpdatePolicy> => {
  try {
    const response = await fetch(`${PWA_UPDATE_POLICY_URL}?ts=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return DEFAULT_PWA_UPDATE_POLICY

    return parsePWAUpdatePolicy(await response.json())
  } catch {
    return DEFAULT_PWA_UPDATE_POLICY
  }
}
